import * as React from 'react';
import { Button } from '../../components/Buttons/Button';
import { L } from '@app/shared/lib/i18n';
import { style } from '../../components/Shared/Style';
import styled from 'lib/styled';
import queryString from 'query-string';
import ReactPasswordStrength from 'react-password-strength';
import { Footer } from '../../components/Footer/Footer';
import { useLocation } from 'react-router-dom';
import { isCordovaBuild } from '../../lib/cordovaHelpers';
import { useEffect, useRef, useState } from 'react';
import Spinner from '../../components/Spinner/Spinner';
import { ChangeCallback, PasswordState } from '../profile/Components/ChangePasswordProfilePage';
import useConfirmOrChangePassword from './hooks/useConfirmOrChangePassword';
import validateToken from './hooks/validateToken';
import Logo from '../../components/Logo/Logo';
import { useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PageBackground } from '../../components/Layout/PageBackground';

export interface PasswordToken {
    token: string;
    invitation_token: string;
}

const Header = styled.div({
    fontWeight: style.fontWeight.bold,
    fontSize: style.fontSize.milli,
    lineHeight: '15px',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    color: style.colors.secondary,
    paddingBottom: style.margin.xs,
});

const StyledLogo = styled(Logo)({
    maxWidth: '300px',
    width: '100%',
    height: '100%',
    maxHeight: '100px',
    placeSelf: 'center',
    backgroundColor: 'inherit',
});

const Error = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: style.margin.l,
});
const ErrorMessage = styled.strong({
    color: 'red',
});

const Sucess = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingBottom: style.margin.l,
});
const SuccessMessage = styled.strong({
    color: style.colors.primary,
});

const Background = styled(PageBackground)({
    width: '100%',
    minHeight: isCordovaBuild ? '-webkit-fill-available' : '100vh',
    backgroundSize: 'cover',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
});

const TextField = styled.div({
    paddingBottom: style.margin.ml,
});

const LoginForm = styled.div({
    width: '80%',
});

const Form = styled.div(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px 12px 40px 12px',
    boxSizing: 'border-box',
    borderRadius: style.roundCornerSize.medium,
    flexDirection: 'column',
    width: '378px',
    [`@media screen and (min-width: 500px)`]: {
        padding: '70px 12px 40px 12px',
        backgroundColor: theme?.colors?.background,
    },
}));

const StyledReactPasswordStrength = styled(ReactPasswordStrength)({
    background: style.colors.inputBackground,
    border: `1px solid ${style.colors.inputBorder}`,
    height: '42px',
    borderRadius: style.roundCornerSize.small,
    outline: 'none',
    fontSize: style.fontSize.seta,
    lineHeight: style.lineHeight.normal,
    '.ReactPasswordStrength-strength-desc': {
        width: '30%',
    },
    '.ReactPasswordStrength-input': {
        padding: `${style.margin.s}px`,
        fontSize: style.fontSize.seta,
        height: '39px',
    },
});

type AwaitedToken =
    | {
          isValid: boolean;
          message: string;
          token?: undefined;
      }
    | {
          isValid: boolean;
          token: string;
          message?: undefined;
      };

export const ConfirmPasswordPage = () => {
    const location = useLocation();
    const history = useHistory();
    const { token: token_url, invitation_token } = queryString.parse(location.search);
    const token = token_url || invitation_token;
    const [validatedToken, setValidatedToken] = useState<AwaitedToken>();

    const [state, setState] = useState<PasswordState>('');
    const [newPassword, setNewPassword] = useState<ChangeCallback | undefined>();
    const [newRepeatPassword, setNewRepeatPassword] = useState<ChangeCallback | undefined>();
    const [isValid, setIsValid] = useState(false);
    const newPasswordRef = useRef<{ clear: () => void }>();
    const newRepeatPasswordRef = useRef<{ clear: () => void }>();

    const { mutate: changePassword, status: changePasswordStatus } = useConfirmOrChangePassword();

    useEffect(() => {
        const getValidatedToken = async () => {
            const awaitedToken = await validateToken(token, location.pathname);
            setValidatedToken(awaitedToken);
        };
        getValidatedToken();
    }, [token]);

    const handleUpdatePassword = () => {
        setState('');
        if (!newPassword || !newRepeatPassword) return;
        if (newPassword.password !== newRepeatPassword.password) {
            return setState('password_not_matched');
        }
        if (!validatedToken?.isValid || !validatedToken.token) return toast.error(`${validatedToken?.message}`);
        changePassword(
            { newPassword: newPassword.password, token: validatedToken.token },
            {
                onSuccess: () => {
                    toast.success('Ditt kopnto är nu klart.');
                    history.push('/');
                },
            }
        );
    };

    useEffect(() => {
        setState('');
        if (!newPassword?.isValid) {
            return setIsValid(false);
        }
        if (!newRepeatPassword?.isValid) {
            return setIsValid(false);
        }

        if (newPassword.password !== newRepeatPassword.password) {
            setState('password_not_matched');
            return setIsValid(false);
        }

        setIsValid(true);
    }, [newPassword, newRepeatPassword]);

    return (
        <Background>
            <Form>
                <StyledLogo />

                <LoginForm>
                    {validatedToken?.message && (
                        <Error>
                            <ErrorMessage>{L(`token_${validatedToken?.message}`)}</ErrorMessage>
                        </Error>
                    )}
                    {state && state !== 'password_changed' && (
                        <Error>
                            <ErrorMessage>{L('reset_' + state)}</ErrorMessage>
                        </Error>
                    )}
                    {state === 'password_changed' && (
                        <Sucess>
                            <SuccessMessage>{L('reset_' + state)}</SuccessMessage>
                        </Sucess>
                    )}

                    <div>
                        <Header>{L('create_password')}</Header>
                        <TextField>
                            <StyledReactPasswordStrength
                                style={{
                                    border: `1px solid ${style.colors.inputBorder}`,
                                }}
                                minLength={5}
                                minScore={2}
                                ref={newPasswordRef}
                                tooShortWord={L('password_short')}
                                changeCallback={(e) => setNewPassword(e)}
                                scoreWords={[
                                    L('password_not_ok'),
                                    L('password_not_ok'),
                                    L('password_ok'),
                                    L('password_strong'),
                                    L('password_great'),
                                ]}
                                inputProps={{ autoComplete: 'off' }}
                            />
                            <p
                                style={{
                                    fontWeight: style.fontWeight.bold,
                                    fontSize: style.fontSize.milli,
                                    color: style.colors.secondary,
                                    marginBottom: style.margin.s,
                                    marginTop: style.margin.xs,
                                }}
                            >
                                {L('password_required_length')}
                            </p>
                        </TextField>
                    </div>
                    <div>
                        <Header>{L('confirm_password')}</Header>
                        <TextField>
                            <StyledReactPasswordStrength
                                style={{
                                    border: `1px solid ${style.colors.inputBorder}`,
                                }}
                                ref={newRepeatPasswordRef}
                                minLength={6}
                                minScore={2}
                                tooShortWord={L('password_short')}
                                scoreWords={[
                                    L('password_not_ok'),
                                    L('password_not_ok'),
                                    L('password_ok'),
                                    L('password_strong'),
                                    L('password_great'),
                                ]}
                                changeCallback={(e) => setNewRepeatPassword(e)}
                                inputProps={{
                                    autoComplete: 'off',
                                }}
                            />
                        </TextField>
                    </div>
                    <div style={{ display: 'grid', width: '100%', marginTop: '1rem' }}>
                        {changePasswordStatus === 'idle' && (
                            <Button
                                onClick={handleUpdatePassword}
                                style={{ width: '100%', justifySelf: 'center' }}
                                variant={'selectedMenu'}
                                disabled={!isValid}
                            >
                                {state ? L(state) : L('confirm_account')}
                            </Button>
                        )}
                        {changePasswordStatus === 'loading' && (
                            <Button
                                style={{
                                    width: '100%',
                                    justifySelf: 'center',
                                    display: 'flex',
                                    justifyContent: 'center',
                                }}
                                variant={'selectedMenu'}
                                disabled={!isValid}
                            >
                                <Spinner />
                            </Button>
                        )}
                        {changePasswordStatus === 'success' && (
                            <Button
                                onClick={handleUpdatePassword}
                                style={{ width: '100%', justifySelf: 'center' }}
                                variant={'selectedMenu'}
                                disabled={!isValid}
                            >
                                {L('confirm_password_success')}
                            </Button>
                        )}
                        {changePasswordStatus === 'error' && (
                            <Button
                                onClick={handleUpdatePassword}
                                style={{ width: '100%', justifySelf: 'center' }}
                                variant={'selectedMenu'}
                                disabled={!isValid}
                            >
                                {L('error')}
                            </Button>
                        )}
                    </div>
                </LoginForm>
            </Form>
        </Background>
    );
};
